import React, { useState, useEffect, useMemo } from 'react'
import { Link } from "react-router-dom";

import './style.scss'
import WorldMap from '../WorldMap/'
import Export from '../Export/'
import BarGraph from '../BarGraph/'
import {casesColors, deathsColors}  from '../colors'
import { useSpring, animated } from 'react-spring'
import { scaleThreshold } from 'd3-scale'
import { extent } from 'd3-array'
import { getD3Formatter } from '../languages'
import classnames from 'classnames'
import isDark from '../isDark'

import { perCapitaMultiplier, perCapitaLabelMaker } from '../formatters'
import { statesByName } from '../data/usStates'
import exportEmbedIcon from '../images/export_icon.png'
import globe from '../images/ic_globe.svg'
import NoDataPattern from '../NoDataPattern'
import { getText } from '../languages'
import close from '../images/ic_close.svg';

import getPopulationSourceLink from '../data/populationSources'

const leftAndRightPaddingTotal = 36;

const metricOptions = [
  { label: 'Cases', shortLabel: 'Cases', metricKey: 'cases' },
  { label: 'Fatalities', metricKey: 'deaths' },
];

export default function RegionBlock(props) {
  const [showExport, setShowExport] = useState(false);
  const [selectedMetricIndex, setSelectedMetricIndex] = useState(0)
  const {
    width,
    regionDataByKey,
    regionType,
    selectedDatum,
    selectedRegionId,
    regionNonZeroValues,
    populations,
    selectedStateAbbr,
    transitioningOut,
    embedParams,
    language,
    isWorld,
    clearForceMetricKey,
    showUpdateMessage,
    setShowUpdateMessage,
    regionsById,
  } = props
  const [revealed, setRevealed] = useState(false)

  const formatNumber = getD3Formatter(language)(',');
  useEffect(() => {
    setTimeout(() => {
      setRevealed(true)
    }, 100)
  }, [])
  const selectedMetric = metricOptions[selectedMetricIndex]

  useEffect(() => {
    if (props.forceMetricKey !== selectedMetric.metricKey) {
      const index = metricOptions.findIndex(d => d.metricKey === props.forceMetricKey)
      if (index !== -1) {
        setSelectedMetricIndex(index)
      }
      if (clearForceMetricKey)  {
        clearForceMetricKey();
      }
    }
  }, [clearForceMetricKey, props.forceMetricKey, selectedMetric.metricKey])

  // Get data for selection
  const { filter, zoom, title, countConfirmed, countDeaths, population, hasData, subnationalUnits, showSubnationalUnits } = useMemo(() => {

    let filter = () => true;
    let zoom = null;
    let title = null;
    let countConfirmed = 0;
    let countDeaths = 0;
    let population = 0;
    let hasData = false;
    let subnationalUnits = [];
    let showSubnationalUnits = false;

    if (regionType === 'county') {
      const selectedStateId = selectedRegionId.substr(0, 2)
      filter = county => county.id.substr(0, 2) === selectedStateId
      zoom = {type: 'county', id: selectedRegionId}
      if (selectedDatum) {
        countConfirmed = selectedDatum.data[selectedDatum.data.length - 1].new_cases_14dayAverage
        countDeaths = selectedDatum.data[selectedDatum.data.length - 1].new_deaths_14dayAverage
        hasData = true
      }
      const populationDatum = populations[selectedRegionId]
      if (populationDatum) {
        population = +populationDatum.POPESTIMATE2019
        title = `${populationDatum.CTYNAME}, ${statesByName[populationDatum.STNAME].abbreviation}`
      }
    } else if (regionType === 'state') {
      const selectedStateId = selectedRegionId.substr(0, 2)
      filter = county => county.id.substr(0, 2) === selectedStateId
      zoom = { type: 'state', id: selectedStateId, highlightCountyId: selectedRegionId }
      const stateData = regionDataByKey[selectedStateAbbr]
      if (stateData) {
        countConfirmed = stateData.data[stateData.data.length - 1].new_cases_14dayAverage
        countDeaths = stateData.data[stateData.data.length - 1].new_deaths_14dayAverage
        hasData = true
      }
      const populationDatum = populations[`${selectedStateId}000`]
      if (populationDatum) {
        population = +populationDatum.POPESTIMATE2019
        title = populationDatum.STNAME
      }
    } else if (regionType === 'usa') {
      const usaData = regionDataByKey['US']
      countConfirmed = usaData.data[usaData.data.length - 1].new_cases_14dayAverage
      countDeaths = usaData.data[usaData.data.length - 1].new_deaths_14dayAverage
      population = +populations['-1'].POPESTIMATE2019
      hasData = true
    } else if (regionType === 'world') {
      const worldData = regionDataByKey['world'];
      countConfirmed = worldData.data[worldData.data.length - 1].new_cases_14dayAverage;
      countDeaths = worldData.data[worldData.data.length - 1].new_deaths_14dayAverage;
      population = worldData.population;
      hasData = true;
    } else if (regionType === 'country') {
      zoom = { type: 'county', id: selectedRegionId };
      title = getText(language, selectedDatum.name);
      countConfirmed = selectedDatum.data[selectedDatum.data.length - 1].new_cases_14dayAverage;
      countDeaths = selectedDatum.data[selectedDatum.data.length - 1].new_deaths_14dayAverage;
      population = selectedDatum.population;
      hasData = true;
      //
      const subnationalUnitsFromParent = Object.keys(regionDataByKey)
        .map(key => regionDataByKey[key])
        .filter(region => region.parent === selectedRegionId);
      const subnationalUnitsFromChild = selectedDatum.subnational
        ? Object.keys(regionDataByKey)
          .map(key => regionDataByKey[key])
          .filter(region => region.parent === selectedDatum.parent)
        : [];
      subnationalUnits = subnationalUnitsFromParent.concat(subnationalUnitsFromChild);
      showSubnationalUnits = subnationalUnits.length > 0;
      if (subnationalUnitsFromParent.length) {
        zoom.type = 'parentCountry';
      }
      if (showSubnationalUnits) {
        // show different UK subunits depending on metric
        const subnationalIds = subnationalUnits.map(region => region.id)
          .filter(regionId => (
            selectedMetric.metricKey === 'cases' && selectedRegionId !== 'GB-ENG'
              ? regionId !== 'GB-ENG'
              : !regionId.includes('UK')
          ));
        filter = region => regionsById[region.id]
          ? !regionsById[region.id].subnational || subnationalIds.includes(region.id)
          : true;
      }
    }

    return { filter, zoom, title, countConfirmed, countDeaths, population, hasData, subnationalUnits, showSubnationalUnits };
  }, [regionType, language, populations, regionsById, regionDataByKey, selectedDatum, selectedMetric.metricKey, selectedRegionId, selectedStateAbbr]);

  const countryLayout = useMemo(() => isWorld && regionType === 'country' && width > 800, [isWorld, regionType, width]);

  const { mapWidth, mapHeight, contentWidth, contentHeight } = useMemo(() => {
    let mapWidth = isWorld ? width : width - leftAndRightPaddingTotal;
    let mapHeight = null;

    if (countryLayout) {
      mapWidth = mapWidth * 0.5;
      mapHeight = mapWidth;
    } else {
      // mapWidth = width;
      if ((regionType === 'county' || regionType === 'state')) {
        // mapWidth = width;
        mapWidth = mapWidth * 0.6;
        mapHeight = mapWidth * (4 / 3)
      }
    }

    const contentWidth = countryLayout ? (mapWidth - 46) : (width - leftAndRightPaddingTotal);
    const contentHeight = countryLayout ? (mapWidth - 46) * 0.62 : null;

    return { mapWidth, mapHeight, contentWidth, contentHeight };
  }, [width, countryLayout, regionType, isWorld]);

  const { dataExtent, casesColorScale, deathsColorScale, colorScale, colors } = useMemo(() => {
    const dataExtent = extent(regionNonZeroValues[selectedMetric.metricKey]);

    let colorScale = null;

    const casesColorScale = scaleThreshold()
      .domain([25, 50, 75, 100, 150, 200, 250, 500, 1000].map(d => d / perCapitaMultiplier ))
      .range(casesColors) //10 colors, so need 9 thresholds

    const deathsColorScale = scaleThreshold()
      .domain([1, 5, 10, 50].map(d => d / perCapitaMultiplier ))
      .range(deathsColors)

    if (selectedMetric.metricKey === 'cases') {
      colorScale = casesColorScale
    } else if (selectedMetric.metricKey === 'deaths') {
      colorScale = deathsColorScale
    }

    const colors = colorScale.range();

    return { dataExtent, casesColorScale, deathsColorScale, colorScale, colors };
  }, [selectedMetric.metricKey, regionNonZeroValues]);

  const mapRightLegend = useMemo(() => {
    if (regionType === 'county' || regionType === 'state') {
      const labels = []
      const colorsBackwards = [...colors].reverse()
      const dividers = colorScale.domain();
      if (dividers) {
        const decimalPlaces = 0;
        colors.forEach((color, colorIndex) => {
          if (colorIndex === 0) {
            let label = perCapitaLabelMaker(dataExtent[0], decimalPlaces, language)
            if (selectedMetric.metricKey === 'deaths') {
              label = `> ${label}`
            }
            labels.push(label)
          } else if (colorIndex === colors.length - 1) {
            labels.push(`> ${perCapitaLabelMaker(dividers[colorIndex - 1], decimalPlaces, language)}`)
          } else {
            labels.push(perCapitaLabelMaker(dividers[colorIndex - 1], decimalPlaces, language))
          }
        })
        labels.reverse()
      }

      const legendSwatchHeight = 23
      const legendSwatchWidth = 15;
      const showZeroDatum = selectedMetric.metricKey === 'deaths'
      const noDataGap = legendSwatchHeight / 2
      const legendHeight = (colorsBackwards.length + (showZeroDatum ? 2 : 1)) * legendSwatchHeight + noDataGap
      const legendWidth = 100
      const tickSize = legendSwatchWidth * 2

      return (
        <div className='rightLegend'>
          <div className='legendTitle'>
            {`${getText(language, selectedMetric.metricKey === 'cases' ? 'casesOfCovid19Per100kPeople' : 'fatalitiesOfCovid19Per100kPeople')} ${getText(language, 'inLast14Days')}`}
          </div>
          <svg width={legendWidth} height={legendHeight}>
            <NoDataPattern />
            {colorsBackwards.map((color, colorIndex) => {
              return (
                <g transform={`translate(0, ${legendSwatchHeight * colorIndex})`} key={color}>
                  <rect width={legendSwatchWidth} height={legendSwatchHeight - 1} fill={color} />
                  <line stroke='#E2E0E0' y1={legendSwatchHeight} y2={legendSwatchHeight} x2={tickSize} />
                  <text y={legendSwatchHeight} x={tickSize + 5} alignmentBaseline='middle'>{labels[colorIndex]}</text>
                </g>
              )
            })}
            {showZeroDatum ? (
              <g transform={`translate(0, ${legendSwatchHeight * colors.length})`} key={'#ffffff'}>
                <rect x={1} width={legendSwatchWidth - 2} height={legendSwatchHeight - 2} fill={'#ffffff'} stroke='#cecece' />
              </g>
            ) : null}
            <g transform={`translate(0, ${(colors.length + (showZeroDatum ? 1 : 0)) * legendSwatchHeight + noDataGap})`}>
              <rect stroke='#cecece' rx='3' fill='url(#noDataPattern' width={legendSwatchWidth} height={legendSwatchWidth} />
              <text x={tickSize + 5} y={legendSwatchWidth / 2} alignmentBaseline='middle'>{getText(language, 'noData')}</text>
            </g>
          </svg>
        </div>
      )
    }
    return null;
  }, [colorScale, colors, dataExtent, language, regionType, selectedMetric.metricKey]);

  const titleAndPopulation = title && regionType !== 'world' ? (
    <div className='titleAndPopulation'>
      <div className='title'>
        {title}
      </div>
      <div className='population'>
        {getText(language, 'population')} {formatNumber(population)}
      </div>
    </div>
  ) : null

  const cases_perCapita =  countConfirmed / population || 0
  const deaths_perCapita = countDeaths / population || 0
  const valuesSpring = useSpring({ cases: countConfirmed || 0, deaths: countDeaths || 0, cases_perCapita, deaths_perCapita})

  const ExportEmbedButton = (props) => {
    const { language } = props
    return (
    <div className={classnames('exportEmbedButton', props.className)} onClick={() => setShowExport(!showExport)}>
      <img src={exportEmbedIcon} alt='' />
      <div className='buttonLabel'>{getText(language, 'exportEmbed')}</div>
    </div>
  )}

  const mapCloseButton = embedParams === null && (regionType !== 'usa' && regionType !== 'world') ? (
    <div className='closeRegionButton' onClick={props.closeRegion}>{getText(language, 'close')}</div>
  ): null
  const isEmbed = embedParams !== null

  const barGraphCapitaValue = selectedMetric.metricKey === 'cases' ? cases_perCapita : deaths_perCapita;
  const barGraphColor = barGraphCapitaValue === 0
    ? '#2E2E2E'
    : selectedMetric.metricKey === 'cases'
      ? '#d1b1b7'
      : '#c9dee0';

  const updateMessage = (
    <div className='label'>
      This map now displays cases and fatalities from the last 14 days. See <Link to='/resources'>Resources</Link> for details. <div className='close' onClick={() => setShowUpdateMessage(false)}><img src={close} alt={getText(language, 'close')} /></div>
    </div>
  );

  const viewWorldMap = (
    <div className='backToWorld' onClick={() => props.goToWorldFromUS()}>
      <img src={globe} alt='' />
      <div>View World Map</div>
    </div>
  );

  const populationSource = getPopulationSourceLink(selectedRegionId)
  let populationLink = null
  if (populationSource) {
    populationLink = (
      <a href={populationSource} target="_blank" rel='noopener noreferrer' className='populationSourceLink'>
        <span className='large'>{getText(language, 'populationSourceLinkText')}</span>
        <span className='small'>{getText(language, 'populationSourceLinkTextSmall')}</span>

      </a>
    )
  }

  return (
    <div className={classnames('RegionBlock', { twoColumnLayout: props.twoColumnLayout, countryLayout, isWorld, isEmbed })} style={{ width }}>
      <div className={classnames('regionBlockSelector', { overhang: isWorld && regionType === 'world' })}>
        <div className='spacer'><div className='firstRow'>&nbsp;</div></div>
        {metricOptions.map((metricOption, metricOptionIndex) => {
          const rawValue = metricOption.metricKey === 'cases' ? countConfirmed : countDeaths
          const perCapitaValue = metricOption.metricKey === 'cases' ? cases_perCapita : deaths_perCapita
          const perCapitaColorScale = metricOption.metricKey === 'cases' ? casesColorScale : deathsColorScale
          const perCapitaBackgroundColor = (perCapitaValue === 0 || rawValue == null || isNaN(rawValue)) ? '#ffffff' : perCapitaColorScale(perCapitaValue)
          const perCapitaColor = isDark(perCapitaBackgroundColor) ? '#fff' : null

          return (
            <div key={metricOptionIndex}>
              <div
                onClick={() => {
                  if (hasData && rawValue != null && !isNaN(rawValue)) {
                    if (clearForceMetricKey) {
                      clearForceMetricKey();
                    }
                    setSelectedMetricIndex(metricOptionIndex);
                  }
                }}
                className={classnames(
                  'button',
                  {
                    selected: metricOptionIndex === selectedMetricIndex,
                    disabled: !(hasData && rawValue != null && !isNaN(rawValue)),
                  },
                )}
              >
                <span>{getText(language, metricOption.label)}</span>
                <span className='valueLabel' style={{ fontSize: '0.8rem', textTransform: 'lowercase', fontWeight: 400 }}>
                  {`  `}
                </span>
                <span className='valueLabel' style={{ fontSize: '0.6rem', textTransform: 'lowercase', fontWeight: 400 }}>
                  {getText(language, 'inLast14Days')}
                </span>
              </div>
              <div className={classnames('value', { selected: selectedMetricIndex === metricOptionIndex })}>
                <animated.span className={classnames({noData: !hasData})}>
                  {valuesSpring[metricOption.metricKey].interpolate(s =>
                    (hasData && rawValue != null && !isNaN(rawValue)) ? formatNumber(Math.round(s)) : getText(language, 'noData')
                  )}
                </animated.span>
                <span className='valueLabel'> {getText(language, metricOption.shortLabel || metricOption.label)}</span>
              </div>
              <div className={classnames('value', { selected: selectedMetricIndex === metricOptionIndex })}>
                <animated.span style={{backgroundColor: perCapitaBackgroundColor, color: perCapitaColor }} className={classnames('perCapitaValue', {noData: !hasData})}>
                  {valuesSpring[`${metricOption.metricKey}_perCapita`].interpolate(s =>
                    (hasData && rawValue != null && !isNaN(rawValue)) ? perCapitaLabelMaker(s) : getText(language, 'noData')
                  )}
                </animated.span>
                <span className='valueLabel'> {getText(language, 'per100k')}</span>
              </div>
            </div>
          )
        })}
        {
          regionType === 'county' && embedParams === null  ?
          (
            <div className='exportEmbedButtonContainer'>
              <ExportEmbedButton className={`${showExport ? 'active' : ''}`} language={language} />
            </div>
          ) : null
        }
        <div className='spacer'>
          <div className='firstRow'>&nbsp;</div>
        </div>

      </div>
      <div className={classnames('shade', {transitioningOut: transitioningOut || ! revealed})} />
      {
        (regionType === 'usa' || regionType === 'world' || regionType === 'country') && embedParams === null ?
        (
          <ExportEmbedButton className={`usa ${showExport ? 'active' : ''}`} language={language}  />
        ) : null
      }
      {
        (regionType === 'usa' && embedParams === null && width > 768) ? viewWorldMap : null
      }
      {countryLayout ? null : titleAndPopulation}
      <div className='columnContainer'>
        {
          ((isWorld || (regionType !== 'usa' && regionType !== 'world')) || !showUpdateMessage) ? null : (
            <div className='updateMessage'>{updateMessage}</div>
          )
        }
        <div
          className={classnames('mapContainer', { overhang: regionType === 'world' })}
          style={countryLayout || (isWorld && !countryLayout) ? null : {
            paddingLeft: leftAndRightPaddingTotal / 2,
            paddingRight: leftAndRightPaddingTotal / 2,
          }}
        >
          <Export
            showExport={showExport}
            setShowExport={setShowExport}
            regionType={regionType}
            language={language}
            directionality={props.directionality}
            isWorld={isWorld}
          />
          <WorldMap
            {...props}
            width={mapWidth}
            height={mapHeight}
            filter={filter}
            zoom={zoom}
            metricKey={selectedMetric.metricKey}
            metricLabel={selectedMetric.label}
            clickRegion={props.clickRegion}
            mapKey={zoom ? zoom.type : 'world'}
            colorScale={colorScale}
            dataExtent={dataExtent}
            language={language}
            isWorld={isWorld}
            subnationalUnits={subnationalUnits}
            showSubnationalUnits={showSubnationalUnits}
          />
          {mapRightLegend}
          {mapCloseButton}
        </div>
        {
          ((isWorld || (regionType === 'usa' || regionType === 'world')) || !showUpdateMessage) ? null : (
            <div className='updateMessage compact'>{updateMessage}</div>
          )
        }
        {
          (regionType === 'usa' && embedParams === null && width <= 768) ? viewWorldMap : null
        }
        <div
          className='barContainer'
          style={countryLayout ? null : {
            paddingLeft: leftAndRightPaddingTotal / 2,
            paddingRight: leftAndRightPaddingTotal / 2,
          }}
        >
          {countryLayout ? titleAndPopulation : null}
          <BarGraph
            contentWidth={contentWidth}
            contentHeight={contentHeight}
            regionType={regionType}
            stateData={regionDataByKey}
            selectedMetric={selectedMetric}
            selectedDatum={selectedDatum}
            population={population}
            barColor={barGraphColor}
            language={language}
            directionality={props.directionality}
            isWorld={isWorld}
            title={title}
          />
        </div>
      </div>
      <div className='dateStamp'>
        <div className='label'>
          {getText(language, 'dataLastUpdated')} {props.dateUpdated} {populationLink}
        </div>
      </div>
    </div>
  )
}
